import type {
	SRT_RowData,
	SRT_TableOptions,
	SRT_TableState,
} from '@/shared/components/master-table'
import type { TableConfigState } from '../../table-config'

import { useMasterReactTable } from '@/shared/components/master-table'
import { useDisableVirtualization } from '@/shared/hooks/useDisableVirtualization'
import { getMasterTableLocale } from '@/shared/i18n'

import { useStore } from '../store'
import { useSyncColumnsConfig } from './useSyncColumnsConfig'

export function useDatasheetTable<T extends SRT_RowData>(
	options: SRT_TableOptions<T> &
		Pick<SRT_TableState<T>, 'isLoading' | 'sorting' | 'searchText'> & {
			onSaveConfig: (
				id: string,
				config: Pick<
					TableConfigState,
					'columnVisibility' | 'columnPinning' | 'columnOrder' | 'columnSizing'
				>
			) => void
		}
) {
	const { isLoading, sorting, onSaveConfig, searchText, ...other } = options
	const disableVirtualization = useDisableVirtualization()
	const columnVisibility = useStore((state) => state.columnVisibility)
	const setColumnVisibility = useStore((state) => state.setColumnVisibility)
	const columnPinning = useStore((state) => state.columnPinning)
	const setColumnPinning = useStore((state) => state.setColumnPinning)
	const columnOrder = useStore((state) => state.columnOrder)
	const setColumnOrder = useStore((state) => state.setColumnOrder)
	const columnSizing = useStore((state) => state.columnSizing)
	const setColumnSizing = useStore((state) => state.setColumnSizing)
	const rowSelection = useStore((state) => state.rowSelection)
	const setRowSelection = useStore((state) => state.setRowSelection)
	const { saveUserColumnsConfig } = useSyncColumnsConfig(onSaveConfig)

	return useMasterReactTable<T>({
		localization: getMasterTableLocale(),
		onColumnVisibilityChange: (updaterOrValue) => {
			setColumnVisibility(updaterOrValue)
			saveUserColumnsConfig()
		},
		onColumnPinningChange: (updaterOrValue) => {
			setColumnPinning(updaterOrValue)
			saveUserColumnsConfig()
		},
		onColumnOrderChange: (updaterOrValue) => {
			setColumnOrder(updaterOrValue)
			saveUserColumnsConfig()
		},
		onColumnSizingChange: (updaterOrValue) => {
			setColumnSizing(updaterOrValue)
			saveUserColumnsConfig()
		},
		onRowSelectionChange: setRowSelection,
		enableVirtualization: !disableVirtualization,
		manualFiltering: true,
		manualPagination: true,
		manualSorting: true,
		enableRowVirtualization: true,
		enableColumnResizing: true,
		enableColumnVirtualization: true,
		columnResizeMode: 'onChange',
		enableTableHead: true,
		enableColumnPinning: true,
		enableHeaderTooltip: true,
		...other,
		state: {
			searchText,
			isLoading,
			sorting,
			rowSelection,
			columnVisibility,
			columnPinning,
			columnOrder,
			columnSizing,
		},
	})
}
